import React from "react";
import { ShieldCheck } from "lucide-react";

const LAST_UPDATED = "07 February 2026";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="text-2xl font-semibold text-brand-blue">{title}</h2>
    <div className="mt-3 space-y-3 text-brand-muted leading-relaxed">{children}</div>
  </section>
);

export default function Privacy() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
      <ShieldCheck size={32} className="text-brand-green mb-4" />
      <div className="overline mb-3">Legal</div>
      <h1 data-testid="privacy-heading" className="text-4xl sm:text-5xl font-semibold text-brand-blue leading-tight">Privacy Policy</h1>
      <p className="mt-3 text-sm text-brand-muted">Last updated: {LAST_UPDATED}</p>

      <p className="mt-8 text-lg text-brand-blue leading-relaxed">
        This Privacy Policy explains how <strong>FuturePath Learning</strong> ("FuturePath", "we", "us", or "our")
        collects, uses, discloses, and protects the personal
        information of parents and, where applicable, children ("you", "your") who access our website and services
        ("Platform"). By using the Platform you agree to this Policy.
      </p>

      <Section title="1. Who we are">
        <p>
          FuturePath Learning is an AI-powered guidance platform helping parents design their child's learning journey.
          For any privacy question, contact us at <a href="mailto:futurepathleaening51@gmail.com" className="text-brand-green underline">futurepathleaening51@gmail.com</a>.
        </p>
      </Section>

      <Section title="2. Information we collect">
        <p>We collect the minimum information required to provide our services:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Account data:</strong> name, email address, and password (stored as a bcrypt hash — never in plain text).</li>
          <li><strong>Assessment data:</strong> information you provide about your child (age, interests, learning style, goals, budget, city, etc.). You may choose what to share.</li>
          <li><strong>Reports & chats:</strong> AI-generated reports and conversations with our AI Parent Assistant.</li>
          <li><strong>Usage data:</strong> pages visited, features used, device type, browser type, approximate location derived from IP.</li>
          <li><strong>Cookies & similar technologies:</strong> for authentication, session management, and analytics.</li>
        </ul>
      </Section>

      <Section title="3. Information about children">
        <p>
          Our Platform is designed for parents and legal guardians. Children do not create their own accounts.
          Parents may voluntarily share information about their child (first name, age, interests) to receive
          personalised guidance. We do not knowingly collect personal information directly from any child under 13
          (or the equivalent age of digital consent in your country). If you believe a child has provided us
          personal information, please contact us and we will delete it promptly.
        </p>
      </Section>

      <Section title="4. How we use your information">
        <ul className="list-disc pl-5 space-y-1.5">
          <li>To generate your personalised assessment reports and curriculum recommendations.</li>
          <li>To power the AI Parent Assistant and remember your prior questions within a session.</li>
          <li>To operate, maintain, secure, and improve the Platform.</li>
          <li>To send transactional communications (account updates, report notifications).</li>
          <li>To comply with legal obligations and enforce our Terms.</li>
        </ul>
        <p>We do <strong>not</strong> sell your personal information to any third party.</p>
      </Section>

      <Section title="5. AI processing">
        <p>
          Assessment answers and chat messages are sent to third-party AI providers (Anthropic Claude) to generate
          personalised responses. Providers process this data solely to return an answer and do not use it to train
          their public models per their zero-retention/enterprise terms. We recommend you avoid sharing highly
          sensitive information (financial, medical) in free-text fields.
        </p>
      </Section>

      <Section title="6. How we share information">
        <p>We share personal information only in these limited ways:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Service providers:</strong> AI providers, cloud hosting, email delivery — under contractual confidentiality.</li>
          <li><strong>Legal compliance:</strong> when required by law, regulation, subpoena, or valid government request.</li>
          <li><strong>Business transfers:</strong> in the event of a merger, acquisition, or sale of assets, with continued protection.</li>
          <li><strong>With your consent:</strong> whenever you explicitly authorise disclosure (e.g., contacting a tutor).</li>
        </ul>
      </Section>

      <Section title="7. Data security">
        <p>
          We use industry-standard measures including bcrypt password hashing, TLS in transit, JWT-based access
          control, and least-privilege access to production systems. No system is 100% secure, but we work hard
          to protect your family's information.
        </p>
      </Section>

      <Section title="8. Data retention">
        <p>
          We retain your account data as long as your account is active. You may request deletion at any time by
          emailing <a href="mailto:futurepathleaening51@gmail.com" className="text-brand-green underline">futurepathleaening51@gmail.com</a>.
          On deletion we remove your account and personal data from active systems within 30 days, except where
          retention is required by law.
        </p>
      </Section>

      <Section title="9. Your rights">
        <p>Depending on your jurisdiction (India DPDP Act, EU GDPR, US state laws), you may have the right to:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Access, correct, or delete your personal data.</li>
          <li>Withdraw consent for optional processing.</li>
          <li>Port your data to another service.</li>
          <li>Lodge a complaint with your local data protection authority.</li>
        </ul>
        <p>To exercise these rights, email <a href="mailto:futurepathleaening51@gmail.com" className="text-brand-green underline">futurepathleaening51@gmail.com</a>.</p>
      </Section>

      <Section title="10. International transfers">
        <p>
          FuturePath is based in India. If you access the Platform from outside India, your information will be
          transferred to and processed in India and other countries where our service providers operate.
        </p>
      </Section>

      <Section title="11. Third-party links">
        <p>
          Our Platform may link to third-party websites, tutors, universities, or communities. We are not
          responsible for their privacy practices — please review their policies before sharing information with them.
        </p>
      </Section>

      <Section title="12. Changes to this policy">
        <p>
          We may update this Policy from time to time. Material changes will be notified via email or in-app
          notice. Continued use of the Platform after the effective date constitutes acceptance.
        </p>
      </Section>

      <Section title="13. Contact us">
        <p>
          Questions or requests regarding this Policy or your data:<br />
          <strong>FuturePath Learning</strong><br />
          Email: <a href="mailto:futurepathleaening51@gmail.com" className="text-brand-green underline">futurepathleaening51@gmail.com</a><br />
          Website: <a href="https://future-path.in" target="_blank" rel="noreferrer" className="text-brand-green underline">future-path.in</a>
        </p>
      </Section>
    </div>
  );
}
